/// <reference types="@serwist/vite/typings" />
import './assets/main.css'

import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { getSerwist } from 'virtual:serwist'
import { useFlagsmith } from 'flagsmith-vue'
import { flagsmithConfig } from '@/config/flagsmith'
import { initializeFlagsmithClient } from '@/config/initializeFlagsmithClient'

import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'

import FloatingVue from 'floating-vue'
import 'floating-vue/dist/style.css'

import { createBottomSheet } from 'bottom-sheet-vue3'
import 'bottom-sheet-vue3/style.css'

import VueVirtualScroller from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

import { usePlayerSettings } from './stores/playerSettings'
import {
  loadAuthToken,
  loadPlayerSettings,
  savePlayerSettings,
  saveAuthToken
} from './helpers/storage'
import { useAppMode } from '@/composables/useAppMode'
import { createI18n } from 'vue-i18n'
import type { Player } from '@acepoker/contract'

import 'vue3-perfect-scrollbar/dist/vue3-perfect-scrollbar.css'
import { en } from './locales/en'
import { useAuth } from './stores/auth'
import { FLAG_NAMES } from './lib/flags'
import App from './App.vue'
import games from 'virtual:acepoker-games'
import { initDataDog, setDatadogUser } from './helpers/integrations/datadog'
import { autoLogin } from './api/autoLogin'
import { loadAssistantModeV2Script } from './helpers/integrations/assistantModeV2'
import { useAuthSession } from './composables/useAuthSession'

const enLocales = Object.values(games)
  .map((data) => data.gameData.locales.en)
  .reduce((acc, cur) => ({ ...acc, ...cur }), {})

const app = createApp(App)

const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  messages: {
    en: {
      ...en,
      ...enLocales
    }
  }
})

app.use(i18n)
app.use(createPinia())
app.use(VueQueryPlugin)
app.use(FloatingVue, {
  themes: {
    'panel-dropdown': {
      $extend: 'dropdown'
    }
  }
})
app.use(Toast, {
  icon: false,
  toastClassName: 'toast__wrapper'
})
app.use(createBottomSheet())
app.use(VueVirtualScroller)

// Initialize Flagsmith
useFlagsmith(flagsmithConfig)
const client = initializeFlagsmithClient()

const playerSettings = usePlayerSettings()
playerSettings.$patch(loadPlayerSettings())
playerSettings.$subscribe((_, state) => {
  savePlayerSettings(state)
})

const { mode } = useAppMode()
watch(
  mode,
  (newValue, oldValue) => {
    if (oldValue) {
      document.body.classList.remove(oldValue)
    }
    document.body.classList.add(newValue)
  },
  { immediate: true }
)

const appMode = 'web'
document.documentElement.classList.add(appMode)
document.body.classList.add(appMode)

function updatePlayerInDatDogg(newPlayer?: Player | null, oldPlayer?: Player | null) {
  if (newPlayer?.player_id) {
    if (!oldPlayer || newPlayer.player_id !== oldPlayer.player_id) {
      setDatadogUser(newPlayer.id?.toString(), {
        player_id: newPlayer.player_id,
        email: newPlayer.email
      })
    }
  } else {
    setDatadogUser(undefined)
  }
}

async function initAuth() {
  const authStore = useAuth()
  const { clearAuthState, processAuthToken } = useAuthSession()
  authStore.status = 'loading'
  try {
    const authToken = loadAuthToken()
    authStore.token = authToken

    if (authToken) {
      await processAuthToken(authToken)
    }

    // Watch for player changes and update Datadog
    watch(
      () => authStore.player,
      (newPlayer, oldPlayer) => {
        updatePlayerInDatDogg(newPlayer, oldPlayer)
      }
    )
  } catch (error) {
    console.error(error)
    clearAuthState()
  } finally {
    // If we don't have a valid token (either never had one or cleared invalid one),
    // attempt autologin to get a guest token
    if (!authStore.token) {
      try {
        const { token } = await autoLogin()
        authStore.token = token
        saveAuthToken(token)

        try {
          await processAuthToken(token)
        } catch (getPlayerError) {
          // NUCLEAR OPTION: autologin succeeded but getPlayer failed
          // This means the autologin token is invalid - clear everything and reload
          console.error('getPlayer failed with autologin token:', getPlayerError)
          clearAuthState()

          console.warn('Autologin token is invalid - reloading app to break infinite loop')
          window.location.reload()
          return
        }
      } catch (autoLoginError) {
        console.error('Autologin failed:', autoLoginError)
        // Don't reload on autologin failure - could be network/server issues
        // Just continue without auth - user can manually login later
      }
    }

    authStore.status = 'loaded'
  }
}

async function initApp() {
  const appElement = document.querySelector('#app')
  if (appElement && '__vue_app__' in appElement) {
    console.warn('App is already mounted, skipping initialization')
    return
  }

  try {
    initDataDog()

    // Load external script based on environment
    const assistantModeV2Url = import.meta.env.VITE_ASSISTANT_MODE_LOADER_URL_V2 || ''

    try {
      await loadAssistantModeV2Script(assistantModeV2Url)
    } catch (error) {
      console.error('Failed to load loader script:', error)
    }

    // Wait for flags to be loaded
    if (client) {
      await client.getFlags()
      const isOnboardingV2 = client.hasFeature(FLAG_NAMES.ONBOARDING_V2)

      // Import router based on flag
      const router = isOnboardingV2
        ? (await import('./router')).router
        : (await import('./router/v1')).routerV1

      await initAuth()

      // Set up global auth error handling
      const { watchAuthErrors } = useAuthSession()
      watchAuthErrors()

      app.use(router)
      app.mount('#app')
      await loadSerwist()
    }
  } catch (error) {
    console.error('App initialization failed:', error)
  }
}

if (import.meta.env.DEV) {
  // Only expose initApp in development
  // @ts-expect-error for debugging purposes
  window.initApp = initApp
}

initApp()

async function loadSerwist() {
  if ('serviceWorker' in navigator) {
    const serwist = await getSerwist()

    serwist?.addEventListener('installed', () => {
      console.log('Serwist installed!')
    })

    void serwist?.register()
  }
}

let lastTouchEnd = 0
document.addEventListener(
  'touchend',
  function (e) {
    const now = new Date().getTime()
    if (now - lastTouchEnd <= 500) {
      e.preventDefault()
    }
    lastTouchEnd = now
  },
  false
)

const MINIMAL_HEIGHT_WITHOUT_ZOOM = 844

let ratio = '0'
if (window.innerHeight < MINIMAL_HEIGHT_WITHOUT_ZOOM - 1) {
  ratio = (window.innerHeight / MINIMAL_HEIGHT_WITHOUT_ZOOM).toFixed(3)
  document.head
    .querySelector('meta[name="viewport"]')
    ?.setAttribute(
      'content',
      `width=device-width, initial-scale=${ratio}, minimum-scale=${ratio}, maximum-scale=${ratio}, user-scalable=no, viewport-fit=cover`
    )
}
